<template>
    <div>
        <div v-if="!fetchingPoolUsers" class="d-flex flex-wrap align-center">
            <ResourcePoolInviteDialog v-if="isResourcePoolManager" @invited="fetchPoolUsers">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn color="primary" class="mr-2" v-bind="attrs" v-on="on">+ user</v-btn>
                </template>
            </ResourcePoolInviteDialog>
            <v-spacer />
            <v-text-field
                class="mx-2"
                v-show="expand"
                placeholder="Search for a user..."
                autofocus
                prepend-inner-icon="mdi-magnify "
                v-model="search"
                solo
                flat
                hide-details
                dense>
                <template v-slot:append>
                    <v-menu offset-y :close-on-content-click="false">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" small class="mr-n2">
                                <v-icon left>mdi-tune-variant</v-icon>
                                <span>Filters</span>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <div v-for="(item, index) in Object.entries(userCheckboxBinders)" :key="index">
                                <v-sheet rounded class="py-2 px-4 my-1">
                                    <v-switch inset dense hide-details class="pa-0 ma-0" v-model="item[1].status">
                                        <template v-slot:label>
                                            <div class="d-flex align-center">
                                                <v-icon left small>{{ item[1].icon }}</v-icon>
                                                <span class="text-capitalize">{{ item[0] }}</span>
                                            </div>
                                        </template>
                                    </v-switch>
                                </v-sheet>
                            </div>
                        </v-list>
                    </v-menu>
                </template>
            </v-text-field>
            <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" @click="fetchPoolUsers" text icon>
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                </template>
                <span>refresh</span>
            </v-tooltip>
        </div>

        <div v-if="!fetchingPoolUsers">
            <v-data-table
                :headers="userHeaders"
                :items="filteredPoolUsers"
                :search="search"
                item-key="uid"
                :items-per-page.sync="itemsPerPage"
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                class="mt-4">
                <template v-slot:top="{ options, pagination, updateOptions }">
                    <div class="d-flex align-center">
                        <v-spacer></v-spacer>
                        <v-data-footer
                            :pagination="pagination"
                            :options="options"
                            @update:options="updateOptions"
                            :itemsPerPageOptions="itemsPerPageOptions"
                            items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                            class="no-border" />
                    </div>
                </template>
                <template v-slot:[`item.type`]="{ item }">
                    <v-icon>{{ organizationRole(normalizedRole(item)).icon }}</v-icon>
                </template>
                <template v-slot:[`item.user`]="{ item }">
                    <div class="d-flex flex-column py-2">
                        <div class="font-weight-medium">
                            {{ item.full_name || item.name || '-' }}
                            <v-chip x-small v-if="item.is_active === true" color="success">Active</v-chip>
                            <v-chip x-small v-else-if="item.is_active === false" color="error">Inactive</v-chip>
                        </div>
                        <span class="caption mr-3 font-weight-bold">{{ item.email || '-' }}</span>
                    </div>
                </template>
                <template v-slot:[`item.role`]="{ item }">
                    <div class="d-flex align-center py-2">
                        <v-chip outlined label small>
                            <v-icon left small>{{ organizationRole(normalizedRole(item)).icon }}</v-icon>
                            <span>{{ organizationRole(normalizedRole(item)).label }}</span>
                        </v-chip>
                    </div>
                </template>
                <template v-slot:no-results>
                    <div class="d-flex flex-column align-center py-8">
                        <v-icon color="info" class="mb-2">mdi-magnify-close</v-icon>
                        <span>No users found for the current filters.</span>
                    </div>
                </template>
                <template v-slot:no-data>
                    <div class="d-flex flex-column align-center py-8">
                        <v-icon color="info" class="mb-2">mdi-account-off-outline</v-icon>
                        <span class="font-weight-medium secondary--text mb-1">No users found</span>
                        <span class="text-center">This resource pool has no additional users yet.</span>
                    </div>
                </template>
            </v-data-table>
        </div>
        <div v-else>
            <v-skeleton-loader class="mx-auto" type="table"></v-skeleton-loader>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { organizationRole } from '@/utils'
import ResourcePoolInviteDialog from '@/modules/user/components/ResourcePoolInviteDialog.vue'

export default {
    name: 'PoolUsers',
    components: { ResourcePoolInviteDialog },
    data() {
        return {
            search: '',
            expand: true,
            fetchingPoolUsers: false,
            poolUsers: [],
            itemsPerPage: 10,
            itemsPerPageOptions: [10, 25, 50, -1],
            userCheckboxBinders: {
                managers: { status: true, icon: organizationRole('manager').icon },
                members: { status: true, icon: organizationRole('member').icon }
            }
        }
    },
    computed: {
        ...mapGetters('userStore', ['isResourcePoolManager']),
        ...mapState('userStore', ['selectedResourcePool', 'availableResourcePools']),
        activeResourcePoolId() {
            const routePoolId = Number(this.$route.params.rpid)
            if (Number.isFinite(routePoolId) && routePoolId > 0) {
                return routePoolId
            }

            const selectedPoolId = Number(this.selectedResourcePool)
            if (Number.isFinite(selectedPoolId) && selectedPoolId > 0) {
                return selectedPoolId
            }

            return Number(this.availableResourcePools[0]?.rpid || 0)
        },
        userHeaders() {
            return [
                { text: 'Type', value: 'type', sortable: false, width: '40px' },
                { text: 'User', value: 'user', sortable: false },
                { text: 'Role', value: 'role', sortable: false }
            ]
        },
        filteredPoolUsers() {
            const displayedRoles = []
            if (this.userCheckboxBinders.managers.status) {
                displayedRoles.push('manager')
            }
            if (this.userCheckboxBinders.members.status) {
                displayedRoles.push('member')
            }

            return this.poolUsers.filter(user => displayedRoles.includes(this.normalizedRole(user)))
        }
    },
    methods: {
        organizationRole,
        normalizedRole(user) {
            const role = (user?.role || user?.Name || user?.resource_pool_role || '').toString().toLowerCase()
            if (role.includes('manager')) {
                return 'manager'
            }
            if (role.includes('faculty')) {
                return 'faculty'
            }
            return 'member'
        },
        async fetchPoolUsers() {
            if (!this.activeResourcePoolId) {
                this.poolUsers = []
                return
            }

            this.fetchingPoolUsers = true
            try {
                const { data } = await this.$axios.get(`/resource/${this.activeResourcePoolId}/list_users`)
                this.poolUsers = Array.isArray(data) ? data : []
            } catch (error) {
                this.poolUsers = []
            } finally {
                this.fetchingPoolUsers = false
            }
        }
    },
    watch: {
        activeResourcePoolId: {
            handler() {
                this.fetchPoolUsers()
            },
            immediate: true
        }
    }
}
</script>
